import { AlertTriangle, Check, ChevronDown, Info, KeyRound, Plus, Shield, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { HttpApiService } from "@/shared/services/httpApi.service";
import type { Permission } from "@/shared/types/permission.types";

import { API_KEY_SCOPE_GROUPS } from "../constants/apiKeyScopes.constants";
import type { AllowedIp, CreateApiKeyRequest, CreateApiKeyResponse } from "../services/apiKeysService";

interface CreateApiKeyFormProps {
  onCreate: (body: CreateApiKeyRequest) => Promise<CreateApiKeyResponse>;
  detectCurrentIp: () => Promise<string | null>;
  onCreated: (result: CreateApiKeyResponse) => void;
  onCancel: () => void;
}

const IPV4_OR_CIDR = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})(?:\/(\d{1,2}))?$/;

function isValidIpv4OrCidr(value: string): boolean {
  const m = value.trim().match(IPV4_OR_CIDR);
  if (!m) return false;
  if ([1, 2, 3, 4].some((i) => Number(m[i]) > 255)) return false;
  if (m[5] && Number(m[5]) > 32) return false;
  return true;
}

const ALL_SCOPES: Permission[] = API_KEY_SCOPE_GROUPS.flatMap((g) => g.scopes.map((s) => s.scope));
const READONLY_SCOPES: Permission[] = ALL_SCOPES.filter((s) => s.endsWith(":read"));

export function CreateApiKeyForm({ onCreate, detectCurrentIp, onCreated, onCancel }: CreateApiKeyFormProps) {
  const { t } = useTranslations("settings");

  const schema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(1, t("api_keys_create_name_required")),
        scopes: z.array(z.string()).min(1, t("api_keys_create_scopes_required")),
      }),
    [t],
  );

  const form = useZodForm(schema, { name: "", scopes: [] });
  const scopes = form.watch("scopes") as Permission[];
  const nameValue = form.watch("name");
  const selectedCount = scopes.length;
  const scopesError = form.formState.errors.scopes?.message;

  const [advOpen, setAdvOpen] = useState(false);
  const [ips, setIps] = useState<AllowedIp[]>([]);
  const [newIp, setNewIp] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [ipError, setIpError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [myIp, setMyIp] = useState<string | null>(null);
  const [isDetectingIp, setIsDetectingIp] = useState(false);
  const ipDetectedRef = useRef(false);

  // Detect the caller's current IP once, the first time the advanced panel opens.
  useEffect(() => {
    if (!advOpen || ipDetectedRef.current) return;
    ipDetectedRef.current = true;
    setIsDetectingIp(true);
    detectCurrentIp()
      .then((ip) => setMyIp(ip && ip !== "unknown" ? ip : null))
      .finally(() => setIsDetectingIp(false));
  }, [advOpen, detectCurrentIp]);

  const myIpAlreadyAdded = !!myIp && ips.some((x) => x.ip === myIp);

  const setScopes = (next: Permission[]) =>
    form.setValue("scopes", next, { shouldValidate: true, shouldDirty: true });

  const toggle = (scope: Permission) => {
    setScopes(scopes.includes(scope) ? scopes.filter((s) => s !== scope) : [...scopes, scope]);
  };

  const addIp = () => {
    const ip = newIp.trim();
    if (!ip) return;
    if (!isValidIpv4OrCidr(ip)) {
      setIpError(t("api_keys_invalid_ip"));
      return;
    }
    if (ips.some((x) => x.ip === ip)) {
      setIpError(t("api_keys_ip_duplicate"));
      return;
    }
    const label = newLabel.trim();
    setIps([...ips, { ip, ...(label ? { label } : {}) }]);
    setNewIp("");
    setNewLabel("");
    setIpError(null);
  };

  const addMyIp = () => {
    if (!myIp) return;
    setNewIp(myIp);
    setIpError(null);
  };

  const onValid = async (values: { name: string; scopes: string[] }) => {
    setSubmitError(null);
    setIsSaving(true);
    try {
      const result = await onCreate({
        name: values.name,
        scopes: values.scopes as Permission[],
        ...(ips.length ? { allowed_ips: ips } : {}),
      });
      onCreated(result);
    } catch (err) {
      setSubmitError(HttpApiService.getErrorMessage(err) || t("api_keys_create_error"));
    } finally {
      setIsSaving(false);
    }
  };

  const groups = API_KEY_SCOPE_GROUPS;

  return (
    <form
      onSubmit={form.handleSubmit(onValid)}
      className="mb-3 rounded-md border border-primary/50 bg-primary/5 p-4 ring-2 ring-primary/10"
    >
      <div className="mb-3.5 flex items-center gap-2">
        <KeyRound size={14} className="text-primary" />
        <span className="text-[13px] font-semibold text-foreground">{t("api_keys_create_title")}</span>
      </div>

      {/* Name */}
      <Controller
        control={form.control}
        name="name"
        render={({ field, fieldState }) => (
          <div className="mb-3">
            <label className="mb-1 block text-[12.5px] font-medium text-foreground">
              {t("api_keys_create_name_label")}
            </label>
            <ColocaInput
              ref={field.ref}
              autoFocus
              value={field.value}
              onValueChange={field.onChange}
              onBlur={field.onBlur}
              placeholder={t("api_keys_create_name_placeholder")}
              className="max-w-[460px] bg-card dark:bg-card"
              aria-invalid={!!fieldState.error}
            />
            {fieldState.error ? (
              <p className="mt-1 flex items-center gap-1.5 text-[11.5px] text-destructive">
                <AlertTriangle size={12} /> {fieldState.error.message}
              </p>
            ) : (
              <p className="mt-1 text-[11.5px] text-muted-foreground">{t("api_keys_create_name_hint")}</p>
            )}
          </div>
        )}
      />

      {/* Scopes */}
      <div className="pt-1">
        <div className="mb-2.5 flex items-end justify-between gap-3">
          <div>
            <div className="text-[13px] font-medium text-foreground">{t("api_keys_create_scopes_label")}</div>
            <div className="mt-0.5 text-[11.5px] text-muted-foreground">
              {t("api_keys_create_scopes_hint")} ·{" "}
              <span className="font-mono text-foreground">
                {t("api_keys_create_scopes_selected", { count: selectedCount })}
              </span>
            </div>
          </div>
          <div className="flex gap-1.5">
            <ColocaButton type="button" variant="ghost" size="xs" onClick={() => setScopes(READONLY_SCOPES)}>
              {t("api_keys_create_quick_readonly")}
            </ColocaButton>
            <ColocaButton type="button" variant="ghost" size="xs" onClick={() => setScopes(ALL_SCOPES)}>
              {t("api_keys_create_quick_all")}
            </ColocaButton>
            <ColocaButton type="button" variant="ghost" size="xs" onClick={() => setScopes([])}>
              {t("api_keys_create_quick_none")}
            </ColocaButton>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {groups.map((group) => {
            const allOn = group.scopes.every((s) => scopes.includes(s.scope));
            return (
              <div key={group.labelKey} className="rounded-md border border-border bg-card p-3">
                <div className="mb-2 flex items-center gap-2 border-b border-dashed border-border pb-2">
                  <span className="font-mono text-[12px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                    {t(group.labelKey)}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const groupScopes = group.scopes.map((s) => s.scope);
                      if (allOn) setScopes(scopes.filter((s) => !groupScopes.includes(s)));
                      else setScopes([...new Set([...scopes, ...groupScopes])]);
                    }}
                    className="ml-auto rounded px-1.5 py-0.5 text-[10.5px] text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    {allOn ? t("api_keys_create_group_clear") : t("api_keys_create_group_select_all")}
                  </button>
                </div>
                <div className="flex flex-col gap-1.5">
                  {group.scopes.map((opt) => {
                    const on = scopes.includes(opt.scope);
                    return (
                      <button
                        key={opt.scope}
                        type="button"
                        onClick={() => toggle(opt.scope)}
                        className="flex items-start gap-2 text-left"
                      >
                        <span
                          className={cn(
                            "mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border",
                            on ? "border-primary bg-primary text-primary-foreground" : "border-border",
                          )}
                          aria-hidden
                        >
                          {on && <Check size={11} strokeWidth={3} />}
                        </span>
                        <span className="min-w-0">
                          <span className="block font-mono text-[11.5px] font-medium text-foreground">
                            {opt.scope}
                          </span>
                          <span className="block text-[11px] text-muted-foreground">{t(opt.descKey)}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {scopesError && (
          <p className="mt-2 flex items-center gap-1.5 text-[11.5px] text-destructive">
            <AlertTriangle size={12} /> {scopesError}
          </p>
        )}
      </div>

      {/* Advanced — IP allowlist */}
      <div className="mt-3.5 overflow-hidden rounded-md border border-border bg-card">
        <button
          type="button"
          onClick={() => setAdvOpen(!advOpen)}
          className={cn(
            "flex w-full items-center gap-2.5 px-3.5 py-3 text-left",
            advOpen && "border-b border-border",
          )}
        >
          <Shield size={14} className="text-muted-foreground" />
          <span className="text-[13px] font-semibold text-foreground">{t("api_keys_create_advanced")}</span>
          {ips.length > 0 && (
            <span className="rounded-full border border-primary/30 bg-primary/10 px-1.5 py-px text-[9.5px] font-semibold text-primary">
              {ips.length}
            </span>
          )}
          <span className="ml-auto text-[11.5px] text-muted-foreground">
            {t("api_keys_create_advanced_hint")}
          </span>
          <ChevronDown
            size={13}
            className={cn("text-muted-foreground transition-transform", advOpen && "rotate-180")}
          />
        </button>

        {advOpen && (
          <div className="p-3.5">
            <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
              {t("api_keys_create_ip_section_label")}
            </div>

            {ips.length > 0 && (
              <div className="mb-2.5 overflow-hidden rounded-sm border border-border">
                {ips.map((entry, i) => (
                  <div
                    key={entry.ip}
                    className={cn(
                      "grid grid-cols-[auto_1fr_auto] items-center gap-2.5 px-3 py-2",
                      i !== ips.length - 1 && "border-b border-border",
                      i % 2 ? "bg-card" : "bg-muted/30",
                    )}
                  >
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
                    <div className="min-w-0 font-mono text-[12.5px] font-semibold text-foreground">
                      {entry.ip}
                      {entry.label && (
                        <span className="ml-2 font-sans text-[11px] font-normal text-muted-foreground">
                          · {entry.label}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setIps(ips.filter((x) => x.ip !== entry.ip))}
                      aria-label={t("api_keys_edit_ips_remove")}
                      className="grid h-6 w-6 place-items-center rounded text-destructive hover:bg-destructive/10"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-[1fr_1fr_auto] gap-1.5">
              <ColocaInput
                value={newIp}
                onValueChange={(v) => {
                  setNewIp(v);
                  if (ipError) setIpError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addIp();
                  }
                }}
                placeholder={t("api_keys_edit_ips_placeholder")}
                aria-invalid={!!ipError}
                className="font-mono"
              />
              <ColocaInput
                value={newLabel}
                onValueChange={setNewLabel}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addIp();
                  }
                }}
                placeholder={t("api_keys_ip_label_placeholder")}
              />
              <ColocaButton
                type="button"
                variant="default"
                icon={Plus}
                onClick={addIp}
                disabled={!newIp.trim()}
              >
                {t("api_keys_edit_ips_add")}
              </ColocaButton>
            </div>

            {ipError ? (
              <p className="mt-1.5 flex items-center gap-1.5 text-[11.5px] text-destructive">
                <AlertTriangle size={12} /> {ipError}
              </p>
            ) : (
              <div className="mt-2 flex items-center gap-2 rounded-sm border border-dashed border-border bg-muted/30 px-3 py-2 text-[11.5px] text-muted-foreground">
                <Info size={12} />
                {isDetectingIp ? (
                  <span>{t("api_keys_my_ip_detecting")}</span>
                ) : myIp ? (
                  <>
                    <span>
                      {t("api_keys_your_current_ip_is")}{" "}
                      <span className="font-mono font-semibold text-foreground">{myIp}</span>
                    </span>
                    {myIpAlreadyAdded ? (
                      <span className="ml-auto flex items-center gap-1 text-primary">
                        <Check size={11} /> {t("api_keys_ip_added")}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={addMyIp}
                        className="ml-auto inline-flex h-6 items-center gap-1 rounded px-2 text-[11.5px] text-foreground hover:bg-foreground/5"
                      >
                        <Plus size={11} /> {t("api_keys_add_my_ip")}
                      </button>
                    )}
                  </>
                ) : (
                  <span>{t("api_keys_my_ip_unavailable")}</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {submitError && (
        <p className="mt-3 flex items-center gap-1.5 text-[11.5px] text-destructive">
          <AlertTriangle size={12} /> {submitError}
        </p>
      )}

      <div className="mt-4 flex items-center gap-2">
        <ColocaButton
          type="submit"
          variant="primary"
          icon={Check}
          loading={isSaving}
          disabled={!nameValue.trim() || selectedCount === 0}
        >
          {t("api_keys_create_submit")}
        </ColocaButton>
        <ColocaButton type="button" variant="ghost" onClick={onCancel} disabled={isSaving}>
          {t("api_keys_create_cancel")}
        </ColocaButton>
        <span className="ml-auto text-[11.5px] text-muted-foreground">{t("api_keys_create_once_hint")}</span>
      </div>
    </form>
  );
}
